import React, { useState } from "react";
import { X, UploadCloud, Bug, Globe, Info, AlertCircle, Stethoscope, Heart } from "lucide-react";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/config";

const AddPestModal = ({ onClose, onSave, pestData = null }) => {
  const [formData, setFormData] = useState({
    name: pestData?.name || "",
    scientificName: pestData?.scientificName || "",
    description: pestData?.description || "",
    cause: pestData?.cause || "",
    symptoms: pestData?.symptoms || "",
    treatments: pestData?.treatments || "",
  });

  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [toast, setToast] = useState(null); // { type: 'error' | 'success', msg: string }
  const isEdit = !!pestData;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const showToast = (msg, type = 'error', ms = 2200) => {
    setToast({ type, msg });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), ms);
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length > 10) {
      showToast("You can upload a maximum of 10 images.");
      return;
    }
    convertHeicIfNeeded(files)
      .then((converted) => setImages((prev) => [...prev, ...converted]))
      .catch(() => setImages((prev) => [...prev, ...files]));
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async (imageFiles) => {
    const uploadPromises = imageFiles.map(async (file, index) => {
      const fileName = `${Date.now()}_${index}_${file.name}`;
      const storageRef = ref(storage, `rice_pests/${formData.name}/${fileName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
            setUploadProgress(progress);
          },
          (error) => reject(error),
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    });

    return Promise.all(uploadPromises);
  };

  // Convert HEIC images to JPEG for preview and upload
  const convertHeicIfNeeded = async (fileList) => {
    const results = [];
    try {
      const mod = await import('heic2any');
      const heic2any = mod.default || mod;
      for (const file of fileList) {
        const isHeic = /heic|heif/i.test(file.type) || /\.heic$|\.heif$/i.test(file.name);
        if (!isHeic) {
          results.push(file);
          continue;
        }
        try {
          const output = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.8 });
          const blobs = Array.isArray(output) ? output : [output];
          for (let i = 0; i < blobs.length; i++) {
            const name = (i === 0 ? file.name : file.name.replace(/(\.[^.]+)?$/, `_${i}$1`)).replace(/\.(heic|heif)$/i, '.jpg');
            const converted = new File([blobs[i]], name, { type: 'image/jpeg' });
            results.push(converted);
          }
        } catch (err) {
          console.warn('HEIC conversion failed, using original file', err);
          results.push(file);
        }
      }
      return results;
    } catch (err) {
      console.warn('heic2any not available, skipping conversion');
      return fileList;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast("Pest name is required.");
      return;
    }

    // Letters only (allow spaces) validation for Pest Name and Scientific Name
    const lettersOnly = /^[A-Za-z\s]+$/;
    if (formData.name && !lettersOnly.test(formData.name.trim())) {
      showToast("Pest name must contain letters only.");
      return;
    }
    if (formData.scientificName && !lettersOnly.test(formData.scientificName.trim())) {
      showToast("Scientific name must contain letters only.");
      return;
    }

    if (!isEdit && images.length === 0) {
      showToast("Please upload at least 1 image for new pests.");
      return;
    }

    // Check for duplicate name across all collections
    try {
      const { collection, query, where, getDocs } = await import("firebase/firestore");
      const { db } = await import("../../firebase/config");
      
      const nameToCheck = formData.name.trim();
      
      // Check in rice_seed_varieties
      const varietiesQuery = query(
        collection(db, "rice_seed_varieties"),
        where("varietyName", "==", nameToCheck),
        where("isDeleted", "==", false)
      );
      const varietiesSnapshot = await getDocs(varietiesQuery);
      
      // Check in rice_local_pests
      const pestsQuery = query(
        collection(db, "rice_local_pests"),
        where("name", "==", nameToCheck),
        where("isDeleted", "==", false)
      );
      const pestsSnapshot = await getDocs(pestsQuery);
      
      // Check in rice_local_diseases
      const diseasesQuery = query(
        collection(db, "rice_local_diseases"),
        where("name", "==", nameToCheck),
        where("isDeleted", "==", false)
      );
      const diseasesSnapshot = await getDocs(diseasesQuery);
      
      // If editing, exclude current pest from duplicate check
      const isDuplicate = 
        varietiesSnapshot.docs.length > 0 ||
        pestsSnapshot.docs.some(doc => isEdit ? doc.id !== pestData?.id : true) ||
        diseasesSnapshot.docs.length > 0;
      
      if (isDuplicate) {
        showToast(`The name "${nameToCheck}" is already used by a variety, pest, or disease. Please choose a different name.`);
        return;
      }
    } catch (error) {
      console.error("Error checking for duplicate name:", error);
      // Continue with submission if check fails
    }

    setUploading(true);

    try {
      let imageUrls = [];
      let mainImageUrl = pestData?.mainImageUrl || "";

      if (images.length > 0) {
        imageUrls = await uploadImages(images);
        mainImageUrl = imageUrls[0];
      }

      const dataToSave = {
        name: formData.name.trim(),
        scientificName: formData.scientificName.trim(),
        description: formData.description.trim(),
        cause: formData.cause.trim(),
        symptoms: formData.symptoms.trim(),
        treatments: formData.treatments.trim(),
        mainImageUrl,
        images: imageUrls.length > 0 ? imageUrls : (pestData?.images || []),
      };

      await onSave(dataToSave, pestData?.id);

      setImages([]);
      setUploadProgress(0);
    } catch (error) {
      showToast(`Error saving pest: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const fieldIcons = {
    name: Bug,
    scientificName: Globe,
    description: Info,
    cause: AlertCircle,
    symptoms: Stethoscope,
    treatments: Heart,
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl w-[28rem] relative flex flex-col max-h-[90vh]">
      {/* Fixed Header */}
      <div className="flex justify-between items-center p-5 border-b border-gray-200 flex-shrink-0">
        <h2 className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent">
          {isEdit ? "Edit Pest" : "Add New Pest"}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-red-500 transition"
          disabled={uploading}
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-5">
        <form id="pest-form" onSubmit={handleSubmit} className="space-y-3">
          {["name", "scientificName", "description", "cause", "symptoms", "treatments"].map((field) => {
            const Icon = fieldIcons[field];
            const label =
              field === "name" ? "Pest Name" :
              field === "scientificName" ? "Scientific Name" :
              field.charAt(0).toUpperCase() + field.slice(1);

            return (
              <div key={field} className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {label} <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center border rounded-lg px-3 focus-within:ring-2 focus-within:ring-green-500 transition">
                  {Icon && <Icon className="w-4 h-4 text-gray-400 mr-2" />}
                  {field === 'description' || field === 'cause' || field === 'symptoms' || field === 'treatments' ? (
                    <textarea
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      className="w-full p-2 outline-none resize-none text-justify"
                      placeholder={`Enter ${label}`}
                      rows={3}
                      disabled={uploading}
                      required
                    />
                  ) : (
                    <input
                      type="text"
                      name={field}
                      value={formData[field]}
                      onChange={handleChange}
                      className="w-full p-2 outline-none"
                      placeholder={`Enter ${label}`}
                      required
                      disabled={uploading}
                    />
                  )}
                </div>
              </div>
            );
          })}

          {/* Show existing images if editing */}
          {isEdit && pestData?.images && pestData.images.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Images</label>
              <div className="grid grid-cols-3 gap-2 mb-2">
                {pestData.images.slice(0, 6).map((imgUrl, idx) => (
                  <div key={idx} className="relative rounded-lg overflow-hidden border">
                    <img
                      src={imgUrl}
                      alt={`current-${idx}`}
                      className="w-full h-20 object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  </div>
                ))}
                {pestData.images.length > 6 && (
                  <div className="flex items-center justify-center rounded-lg border bg-gray-100">
                    <span className="text-xs text-gray-500">+{pestData.images.length - 6} more</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isEdit ? "Upload New Images (Optional)" : "Upload Images"}
              {!isEdit && <span className="text-red-500">*</span>}
            </label>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-5 cursor-pointer hover:border-green-500 transition">
              <UploadCloud className="w-10 h-10 text-gray-400 mb-2" />
              <span className="text-gray-500 text-sm text-center">
                Click or drag files here {isEdit ? "(Optional)" : "(Required - Max 10 images)"}
              </span>
              <input
                type="file"
                multiple
                  accept="image/*,.heic,.HEIC,.heif,.HEIF"
                className="hidden"
                onChange={handleImageChange}
                disabled={uploading}
                required={!isEdit}
              />
            </label>

            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {images.map((img, idx) => (
                  <div key={idx} className="relative rounded-lg overflow-hidden border">
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`preview-${idx}`}
                      className="w-full h-20 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(idx)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      disabled={uploading}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="w-full">
              <div className="flex justify-between text-sm mb-1">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Fixed Footer */}
      <div className="flex justify-end gap-2 p-5 border-t border-gray-200 flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
          disabled={uploading}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="pest-form"
          className="px-4 py-1.5 rounded-lg bg-gradient-to-r from-green-600 to-emerald-500 text-white font-medium hover:opacity-90 shadow-md transition disabled:opacity-50"
          disabled={uploading}
        >
          {uploading ? "Saving..." : (isEdit ? "Update" : "Save")}
        </button>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-[60]">
          <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 border ${
            toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'
          }`}>
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm font-medium">{toast.msg}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-1 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPestModal;